<template>
  <v-card :elevation="0">
    <v-row>
      <v-col>
        Do you want to send a message to the client?
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-radio-group
          v-model="shouldSendNotificationChoice"
          :error="v$.shouldSendNotificationChoice.$error"
          row
          @change="value => setShouldSendNotification(value)"
        >
          <v-radio
            label="Yes"
            value="yes"
          />
          <v-radio
            label="No"
            value="no"
          />
        </v-radio-group>

        <p
          v-if="v$.shouldSendNotificationChoice.$error"
          class="error--text"
        >
          You have to choose an option.
        </p>
      </v-col>
    </v-row>

    <template v-if="shouldSendNotification">
      <v-row>
        <v-col>
          <v-select
            v-model="recipientId"
            label="Who do you want to send the message to?"
            :item-text="item => `${item.first_name} ${item.middle_name} ${item.last_name}`"
            item-value="id"
            :items="legalCase.parties"
            :error="v$.recipientId.$error"
            filled
            @change="value => changeRecipientId(value)"
          />

          <p
            v-if="v$.recipientId.$error"
            class="error--text"
          >
            You have to pick a recipient.
          </p>
        </v-col>
      </v-row>

      <v-row v-if="recipient">
        <v-col>
          <p>How would you like to send this message?</p>

          <v-checkbox
            v-model="channels"
            :disabled="phoneDisabled"
            :error="v$.channels.$error"
            :label="phoneLabel()"
            value="text"
            @change="value => changeChannels(value)"
          />

          <v-checkbox
            v-if="emailNotificationEnabled"
            v-model="channels"
            :disabled="!recipientHasEmail"
            :error="v$.channels.$error"
            :label="emailLabel()"
            value="email"
            @change="value => changeChannels(value)"
          />

          <p
            v-if="v$.channels.$error"
            class="error--text"
          >
            You have to pick at least one channel.
          </p>

          <template v-if="!recipientHasPreferredTextNumber && !recipientHasEmail">
            <p>
              The select party has no contact information. Please cancel the closing process, update the party accordingly and come back to proceed.
            </p>
          </template>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-textarea
            v-model="message"
            :counter="maxMessageSize"
            :error-messages="getErrorMessages(v$.message)"
            filled
            :label="messageLabel"
            @blur="v$.message.$touch()"
            @input="v$.message.$touch()"
          />

          <v-btn
            depressed
            @click="showTemplateSelector = true"
          >
            Load message from Template
          </v-btn>

          <TextMessageEditorTemplateSelector
            v-if="showTemplateSelector"
            v-model="showTemplateSelector"
            @set-message-text="text => setMessageText(text)"
          />
        </v-col>
      </v-row>
    </template>
  </v-card>
</template>

<script>
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import useVuelidate from '@vuelidate/core'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'

import TextMessageEditorTemplateSelector from '@/components/DocketEntry/TextMessageEditor/TextMessageEditorTemplateSelector.vue'

export default {
  name: 'CaseClosingNotification',

  components: {
    TextMessageEditorTemplateSelector,
  },

  props: {
    legalCase: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      channels: [],
      message: null,
      recipient: null,
      recipientId: null,
      shouldSendNotificationChoice: null,
      showTemplateSelector: false,
    }
  },

  validations () {
    return {
      channels: {
        required: helpers.withMessage('Pick at least one channel', requiredIf(this.shouldSendNotification)),
      },

      message: {
        required: helpers.withMessage('Provide the message to send', requiredIf(this.shouldSendNotification)),
        size: helpers.withMessage('The message can not exceed 1200 characters', (value, _, vm) => {
          if (!vm.channels.includes('text')) return true
          return value.length < 1200
        }),
      },

      recipientId: {
        required: helpers.withMessage('Pick a recipient', requiredIf(this.shouldSendNotification)),
      },

      shouldSendNotificationChoice: { required },
    }
  },

  computed: {
    isTwilioSettingsSetup () {
      return !!this.features?.twilio?.enabled
    },

    maxMessageSize () {
      if (this.channels.includes('text')) return 1200
      return null
    },

    messageLabel () {
      let label = 'What would you like the message to say?'
      if (this.maxMessageSize) label += ` (max. ${this.maxMessageSize} characters)`
      return label
    },

    phoneDisabled () {
      return !this.recipientHasPreferredTextNumber || !this.isTwilioSettingsSetup
    },

    recipientEmail () {
      return this.recipient.emails.find(email => email.primary === true) || this.recipient.emails[0]
    },

    recipientHasEmail () {
      return !!this.recipientEmail
    },

    recipientHasPreferredTextNumber () {
      return !!this.recipientPreferredTextNumber
    },

    recipientPreferredTextNumber () {
      return this.recipient.phones.find(phone => phone.preferred_text === true)
    },

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'emailNotificationEnabled',
      'shouldSendNotification',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),
  },

  watch: {
    message (value) {
      this.setClosingNotificationMessage(value)
    },

    recipientId: function (newValue, _) {
      this.recipient = this.legalCase.parties.find(party => party.id === newValue)
    },

    shouldSendNotificationChoice: function (value) {
      this.setShouldSendNotification(value === 'yes')
    },

    'v$.$invalid': {
      handler (value) {
        this.$emit('update-step-validity', !value)
      },

      immediate: true,
    },
  },

  created () {
    this.getFeatures()
    this.getEmailNotificationEnabled()
  },

  methods: {
    changeChannels (value) {
      this.setClosingNotificationChannels(value)
    },

    changeRecipientId (value) {
      this.channels = []
      this.resetClosingNotificationChannels(null)
      this.setClosingNotificationRecipientId(value)
    },

    emailLabel () {
      if (this.recipientHasEmail) return `E-mail to (${this.recipientEmail.address})`
      return 'The select party does not have email.'
    },

    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    phoneLabel () {
      if (!this.isTwilioSettingsSetup) return 'Twilio is not set up. You can not send a text to this party.'
      if (this.recipientHasPreferredTextNumber) return `Text message to primary text number (${this.recipientPreferredTextNumber.phone_number})`
      return 'The selected party does not have a preferred text number.'
    },

    setMessageText (text) {
      this.message = text
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'getEmailNotificationEnabled',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
    ]),

    ...mapMutations(LEGAL_CASE_NAMESPACE, [
      'resetClosingNotificationChannels',
      'setClosingNotificationChannels',
      'setClosingNotificationMessage',
      'setClosingNotificationRecipientId',
      'setShouldSendNotification',
    ]),
  },
}
</script>
